import { InfiniteData, UseSuspenseInfiniteQueryResult } from "@tanstack/react-query";
import { FreeMode, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { useTranslation } from "react-i18next";

import AvatarCard from "@/components/cards/AvatarCard";
import Section from "@/components/helpers/Section/Section";
import SectionSkeleton from "@/components/helpers/Section/Section.skeleton";
import IconArrowLeft from "@/components/icons/IconArrowLeft";

import { Persons } from "@/api/content/content.types";
import { ROUTES } from "@/config/routes";

const SearchPersonsSection = ({
  persons,
}: {
  persons: UseSuspenseInfiniteQueryResult<InfiniteData<Persons, unknown>, Error>;
}) => {
  const { t } = useTranslation();

  const { data, isPending } = persons;

  if (isPending) {
    return <SectionSkeleton type={"channels"} title={t("actors-title")} />;
  }

  if (data.pages[0].results.length == 0) return <></>;

  return (
    <Section title={t("actors-title")} className={"group/actors !mt-0"}>
      <div className={"relative"}>
        <Swiper
          slidesPerView={3}
          slidesPerGroup={3}
          spaceBetween={15}
          mousewheel={{
            forceToAxis: true,
          }}
          speed={500}
          navigation={{
            prevEl: ".js-actors-section-prev",
            nextEl: ".js-actors-section-next",
          }}
          modules={[Navigation, FreeMode]}
          breakpoints={{
            640: {
              slidesPerView: 5,
              slidesPerGroup: 5,
              spaceBetween: 12,
            },
            1024: {
              slidesPerView: 10,
              slidesPerGroup: 10,
              spaceBetween: 12,
            },
            1440: {
              slidesPerView: 12,
              slidesPerGroup: 12,
              spaceBetween: 15,
            },
          }}
          className={"responsive-transition overflow-visible"}
        >
          {data.pages[0].results.map((person) => {
            return (
              <SwiperSlide key={person.id}>
                <AvatarCard
                  type={"round-1"}
                  link={ROUTES.PERSON_DETAILS.replace(":id", String(person.id))}
                  imageSrc={person?.picture?.small}
                  name={person.name}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
        <button
          type={"button"}
          title={t("navigation.previous")}
          style={{ background: "linear-gradient(270deg, rgba(0, 0, 0, 0.00) 7.17%, #000 97.86%)" }}
          className={
            "responsive-transition js-actors-section-prev group invisible absolute -left-20 top-0 z-[100] flex h-full w-20 items-center  p-5 opacity-0 hover:bg-orange-1 disabled:!invisible disabled:!opacity-0 group-hover/actors:visible group-hover/actors:opacity-100 max-lg:hidden"
          }
        >
          <IconArrowLeft name={"arrow-right"} className={"size-10  stroke-on-primary"} />
        </button>
        <button
          type={"button"}
          title={t("navigation.next")}
          style={{ background: "linear-gradient(90deg, rgba(0, 0, 0, 0.00) 7.17%, #000 97.86%)" }}
          className={
            "responsive-transition js-actors-section-next group invisible absolute -right-20 top-0 z-[100] flex h-full w-20 items-center  p-5 opacity-0 hover:bg-orange-1 disabled:!invisible disabled:!opacity-0 group-hover/actors:visible group-hover/actors:opacity-100 max-lg:hidden"
          }
        >
          <IconArrowLeft name={"arrow-right"} className={"size-10 rotate-180 stroke-on-primary"} />
        </button>
      </div>
    </Section>
  );
};

export default SearchPersonsSection;
